import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';

import { FolderAssetType } from '@bingads-webui-campaign-react/asset-library-model';
import { AddMediaLandingPage } from './landing-page-content';

const ManagedLandingView = ({
  addMediaConfig,
  customMediaSourceConfig,
  maxUploadNum,
  minSize,
  processUploadedImage,
  onProcessUploadComplete,
  goToView,
  setHideSubtitle,
  assetLibraryConfig,
  setErrors,
  i18n,
  perfMarker,
  setShowApplyButton,
  setDisableApplyButton,
  triggerAddImagesRef,
  assetLibraryDataService,
  imageExperienceProps,
  imageType,
  setSelectionInfoText,
  allowedAssetType,
  isAssetLibraryAIGCMVPEnabled,
  logger,
  isClickableOutsideFocusTrap,
  preDefinedAssetSources,
  disabledAssetSources,
  eventHandler,
  supportImageUpscale,
  minUploadSize,
  showPromotionBanner,
  isAdStudioEnabled,
}) => {
  const finalConfig = useMemo(
    () => {
      if (_.isFunction(customMediaSourceConfig)) {
        return customMediaSourceConfig({ addMediaConfig });
      }

      return customMediaSourceConfig || addMediaConfig;
    },
    [addMediaConfig, customMediaSourceConfig]
  );
  const landingPageButtonConfig =
    _.compact(_.pluck(_.values(finalConfig), 'buttonConfig'));

  useEffect(() => {
    // set default state
    setShowApplyButton(false);
    setDisableApplyButton(true);
    setErrors([]);
  }, [setDisableApplyButton, setErrors, setShowApplyButton]);

  return (
    <AddMediaLandingPage
      goToView={goToView}
      setHideSubtitle={setHideSubtitle}
      maxUploadNum={maxUploadNum}
      minSize={minSize}
      processUploadedImage={processUploadedImage}
      onProcessUploadComplete={onProcessUploadComplete}
      buttonConfig={landingPageButtonConfig}
      assetLibraryConfig={assetLibraryConfig}
      setErrors={setErrors}
      i18n={i18n}
      showPromotionBanner={showPromotionBanner}
      perfMarker={perfMarker}
      setShowApplyButton={setShowApplyButton}
      setDisableApplyButton={setDisableApplyButton}
      triggerAddImagesRef={triggerAddImagesRef}
      assetLibraryDataService={assetLibraryDataService}
      imageExperienceProps={imageExperienceProps}
      imageType={imageType}
      setSelectionInfoText={setSelectionInfoText}
      allowedAssetType={allowedAssetType}
      isAssetLibraryAIGCMVPEnabled={isAssetLibraryAIGCMVPEnabled}
      logger={logger}
      isClickableOutsideFocusTrap={isClickableOutsideFocusTrap}
      preDefinedAssetSources={preDefinedAssetSources}
      disabledAssetSources={disabledAssetSources}
      eventHandler={eventHandler}
      supportImageUpscale={supportImageUpscale}
      minUploadSize={minUploadSize}
      isAdStudioEnabled={isAdStudioEnabled}
    />
  );
};

ManagedLandingView.propTypes = {
  addMediaConfig: PropTypes.shape({}).isRequired,
  maxUploadNum: PropTypes.number.isRequired,
  minSize: PropTypes.arrayOf(PropTypes.number).isRequired,
  assetLibraryConfig: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  processUploadedImage: PropTypes.func.isRequired,
  onProcessUploadComplete: PropTypes.func,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  showPromotionBanner: PropTypes.bool,
  customMediaSourceConfig: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
  goToView: PropTypes.func.isRequired,
  setHideSubtitle: PropTypes.func.isRequired,
  setErrors: PropTypes.func,
  setShowApplyButton: PropTypes.func.isRequired,
  setDisableApplyButton: PropTypes.func.isRequired,
  triggerAddImagesRef: PropTypes.shape({}).isRequired,
  assetLibraryDataService: PropTypes.shape({}).isRequired,
  imageExperienceProps: PropTypes.shape({}).isRequired,
  imageType: PropTypes.string,
  setSelectionInfoText: PropTypes.func.isRequired,
  allowedAssetType: PropTypes.string,
  isAssetLibraryAIGCMVPEnabled: PropTypes.bool,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  isClickableOutsideFocusTrap: PropTypes.bool,
  preDefinedAssetSources: PropTypes.arrayOf(PropTypes.shape({})),
  disabledAssetSources: PropTypes.arrayOf(PropTypes.string),
  eventHandler: PropTypes.shape({}),
  supportImageUpscale: PropTypes.bool,
  minUploadSize: PropTypes.arrayOf(PropTypes.number),
};

ManagedLandingView.defaultProps = {
  customMediaSourceConfig: null,
  setErrors: _.noop,
  imageType: null,
  allowedAssetType: FolderAssetType.Image,
  isAssetLibraryAIGCMVPEnabled: false,
  onProcessUploadComplete: _.noop,
  isClickableOutsideFocusTrap: false,
  preDefinedAssetSources: [],
  disabledAssetSources: [],
  eventHandler: null,
  supportImageUpscale: false,
  showPromotionBanner: true,
  minUploadSize: [],
};

export const getLandingPageConfig = (isAssetLibraryAIGCMVPEnabled, isLiteLandingPageEnabled, isAdStudioEnabled, showPromotionBanner, panelTitle, panelSubtitle) => {
  let title = _TL_('Asset library');
  let subTitle = '';
  if (panelTitle) {
    title = panelTitle;
  } else if (isLiteLandingPageEnabled) {
    title = _TL_('Add image');
  } else if (isAdStudioEnabled) {
    title = _TL_('Ads Studio');
  }

  if (panelSubtitle) {
    subTitle = panelSubtitle;
  } else if (isLiteLandingPageEnabled) {
    subTitle = _TL_('Choose an image from our recommendation, or browse to add a new one.');
  }

  return ({
    title,
    onRenderSubtitle: ({
      subtitleClass,
      i18n,
      aid,
      cid,
      onAssetLibraryLinkClick,
    }) => ( // eslint-disable-line react/prop-types
      <div className={classnames(subtitleClass, 'landing-page-subtitle')}>
        {subTitle ? (
          i18n.getString(subTitle)
        ) : (
          <React.Fragment>
            {isAssetLibraryAIGCMVPEnabled
              ? i18n.getString(_TL_('You can create assets, choose assets you\'ve already uploaded to '))
              : i18n.getString(_TL_('Choose assets you\'ve already uploaded to '))}
            <a
              href={`/campaign/vnext/${isAdStudioEnabled ? 'adstudio/home' : 'assetlibrary'}?aid=${aid}&cid=${cid}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onAssetLibraryLinkClick ? () => { onAssetLibraryLinkClick(isAdStudioEnabled); } : _.noop}
            >
              {isAdStudioEnabled ? i18n.getString(_TL_('Ads Studio')) : i18n.getString(_TL_('Asset Library'))}
            </a>
            {i18n.getString(_TL_(' or browse to add a new file.'))}
          </React.Fragment>
        )}
      </div>
    ),
    showBackButton: false,
    onRenderContent: props => (
      <ManagedLandingView
        {...props}
        isAdStudioEnabled={isAdStudioEnabled}
        showPromotionBanner={showPromotionBanner}
      />
    ),
  });
};
